import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedList } from 'react-intl';

import { LabelM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { formatWord } from '@sorare/core/src/lib/humanize';

import type { OddsDescription_cardShardsChest } from './__generated__/index.graphql.ts';

type Props = {
  cardShardsChest: OddsDescription_cardShardsChest;
};

export const OddsDescription = ({ cardShardsChest }: Props) => {
  const { formatMessage, formatNumber } = useIntlContext();
  const odds = cardShardsChest.thresholds.map(({ name, probability }) =>
    formatMessage(
      {
        defaultMessage: '{percentage} chance of getting a {name} draw',
      },
      {
        percentage: formatNumber(probability / 10000, {
          style: 'percent',
          maximumFractionDigits: 2,
        }),
        name: formatWord(name),
      }
    )
  );

  return (
    <LabelM color="var(--c-nd-600)">
      <FormattedMessage
        defaultMessage="* You have {odds}."
        values={{ odds: <FormattedList type="conjunction" value={odds} /> }}
      />
    </LabelM>
  );
};

OddsDescription.fragments = {
  cardShardsChest: gql`
    fragment OddsDescription_cardShardsChest on CardShardsChest {
      id
      thresholds {
        name
        probability
      }
    }
  ` as TypedDocumentNode<OddsDescription_cardShardsChest>,
};
